import {useQuery} from '@apollo/client';
import * as React from 'react';

import {FeedPage, mergeFeedPages} from '../socialFeed';
import {QUERY_STUDENT_SOCIAL} from '../studentQueries';
import {StudentTheme} from '../studentTheme';

import {PostCard} from './PostCard';

interface Props {
  theme: StudentTheme;
}

interface SocialData {
  social?: FeedPage | null;
}

const PAGINA = 20;

export const SocialScreen: React.FC<Props> = ({theme}) => {
  const {data, loading, error, fetchMore, refetch} = useQuery<SocialData>(QUERY_STUDENT_SOCIAL, {
    variables: {first: PAGINA},
  });

  const feed = data?.social;
  const [carregandoMais, setCarregandoMais] = React.useState(false);
  const [falhouPaginacao, setFalhouPaginacao] = React.useState(false);

  // O observer le o estado por ref, e nao por dependencia do efeito. Ligar o
  // efeito ao callback fazia cada setState recriar o observer, que disparava
  // na hora (a sentinela continua visivel) e chamava de novo — laco infinito
  // que congelava a aba.
  const feedRef = React.useRef<FeedPage | null | undefined>(feed);
  feedRef.current = feed;

  const carregandoRef = React.useRef(false);
  const cursorPedidoRef = React.useRef<string | null>(null);

  const carregarMais = React.useCallback(async () => {
    const atual = feedRef.current;

    if (!atual?.hasNextPage || !atual.endCursor) return;
    if (carregandoRef.current) return;
    // Servidor que devolvesse o mesmo cursor faria o feed pedir a mesma pagina
    // para sempre; parar no segundo pedido igual.
    if (cursorPedidoRef.current === atual.endCursor) return;

    carregandoRef.current = true;
    cursorPedidoRef.current = atual.endCursor;
    setCarregandoMais(true);

    try {
      await fetchMore({
        variables: {first: PAGINA, after: atual.endCursor},
        updateQuery: (anterior, {fetchMoreResult}) => {
          if (!fetchMoreResult?.social) return anterior;

          return {social: mergeFeedPages(anterior?.social ?? undefined, fetchMoreResult.social)};
        },
      });
    } catch {
      // Sem isso, uma falha de rede reabriria a tentativa a cada frame.
      setFalhouPaginacao(true);
    } finally {
      carregandoRef.current = false;
      setCarregandoMais(false);
    }
  }, [fetchMore]);

  const carregarMaisRef = React.useRef(carregarMais);
  carregarMaisRef.current = carregarMais;

  const sentinelaRef = React.useRef<HTMLDivElement | null>(null);
  const temPosts = (feed?.list?.length ?? 0) > 0;

  // IntersectionObserver em vez de listener de scroll: nao dispara reflow a
  // cada pixel. Criado uma unica vez, quando a sentinela passa a existir.
  React.useEffect(() => {
    const alvo = sentinelaRef.current;
    if (!alvo || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((entrada) => entrada.isIntersecting)) carregarMaisRef.current();
      },
      {rootMargin: '400px'},
    );

    observer.observe(alvo);

    return () => observer.disconnect();
  }, [temPosts]);

  if (loading && !feed) {
    return <p className="student-screen__estado">Carregando…</p>;
  }

  if (error && !feed) {
    return (
      <div className="student-screen__estado">
        <p>Não foi possível carregar o feed.</p>
        <button type="button" className="student-app__retry" onClick={() => refetch()}>
          Tentar de novo
        </button>
      </div>
    );
  }

  const posts = feed?.list ?? [];

  if (posts.length === 0) {
    return <p className="student-screen__estado">Nenhuma publicação por aqui ainda.</p>;
  }

  return (
    <div className="social-feed">
      {posts.map((post) => (
        <PostCard key={post.id} post={post} theme={theme} />
      ))}

      {feed?.hasNextPage && !falhouPaginacao && (
        <div ref={sentinelaRef} className="social-feed__sentinela" aria-hidden="true" />
      )}

      {carregandoMais && <p className="student-screen__estado">Carregando mais…</p>}

      {falhouPaginacao && (
        <div className="student-screen__estado">
          <p>Não foi possível carregar mais publicações.</p>
          <button
            type="button"
            className="student-app__retry"
            onClick={() => {
              setFalhouPaginacao(false);
              cursorPedidoRef.current = null;
              carregarMaisRef.current();
            }}
          >
            Tentar de novo
          </button>
        </div>
      )}
    </div>
  );
};
